import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    TrendingUp,
    BarChart2,
    Smartphone,
    Shield,
    Users,
    Globe,
    Clock,
    Zap,
    ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';

const FeaturesPage = () => {
    const navigate = useNavigate();

    const allFeatures = [
        {
            icon: <BarChart2 className="w-8 h-8" />,
            title: "Advanced Charting",
            description: "Professional-grade technical analysis tools with over 50 indicators and drawing tools.",
            color: "text-blue-600",
            bg: "bg-blue-50"
        },
        {
            icon: <Zap className="w-8 h-8" />,
            title: "Real-Time Data",
            description: "Live streaming quotes for thousands of US stocks, ETFs, and major indices.",
            color: "text-yellow-600",
            bg: "bg-yellow-50"
        },
        {
            icon: <Smartphone className="w-8 h-8" />,
            title: "Mobile Optimized",
            description: "Trade on the go with our fully responsive mobile interface that works on any device.",
            color: "text-purple-600",
            bg: "bg-purple-50"
        },
        {
            icon: <Shield className="w-8 h-8" />,
            title: "Risk Management",
            description: "Practice stop-loss and paper trading execution without risking real capital.",
            color: "text-green-600",
            bg: "bg-green-50"
        },
        {
            icon: <Users className="w-8 h-8" />,
            title: "Social Trading",
            description: "Share your portfolio with friends, compete on leaderboards, and learn from others.",
            color: "text-indigo-600",
            bg: "bg-indigo-50"
        },
        {
            icon: <Globe className="w-8 h-8" />,
            title: "Global Markets",
            description: "Access to major US exchanges (NYSE, NASDAQ) with plans for international expansion.",
            color: "text-cyan-600",
            bg: "bg-cyan-50"
        },
        {
            icon: <Clock className="w-8 h-8" />,
            title: "Market History",
            description: "Analyze historical performance with data going back 5+ years for detailed backtesting.",
            color: "text-orange-600",
            bg: "bg-orange-50"
        },
        {
            icon: <TrendingUp className="w-8 h-8" />,
            title: "Portfolio Analytics",
            description: "Deep dive into your performance with beta, alpha, and Sharpe ratio metrics.",
            color: "text-pink-600",
            bg: "bg-pink-50"
        }
    ];

    return (
        <div className="min-h-screen bg-[#F9F9F7]">
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-stone-200/50">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-[#1A4D2E] rounded-lg flex items-center justify-center">
                                <TrendingUp className="w-5 h-5 text-white" />
                            </div>
                            <span className="font-serif text-xl font-semibold text-[#1A4D2E]">FictoInvest</span>
                        </Link>
                        <nav className="hidden md:flex items-center gap-8">
                            <Link to="/features" className="text-sm font-medium text-[#1A4D2E] transition-colors">Features</Link>
                            <Link to="/pricing" className="text-sm font-medium text-gray-600 hover:text-[#1A4D2E] transition-colors">Pricing</Link>
                            <Link to="/about" className="text-sm font-medium text-gray-600 hover:text-[#1A4D2E] transition-colors">About</Link>
                            <Link to="/contact" className="text-sm font-medium text-gray-600 hover:text-[#1A4D2E] transition-colors">Contact</Link>
                        </nav>
                        <div className="flex items-center gap-3">
                            <Button
                                variant="ghost"
                                onClick={() => navigate('/login')}
                                className="text-[#1A4D2E] hover:bg-green-50"
                            >
                                Log In
                            </Button>
                            <Button
                                onClick={() => navigate('/register')}
                                className="bg-[#1A4D2E] hover:bg-[#143d24] text-white shadow-lg shadow-green-900/20"
                            >
                                Sign Up
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="pt-32 pb-20 px-6 md:px-12 bg-white">
                <div className="max-w-4xl mx-auto text-center space-y-6">
                    <h1 className="font-serif text-4xl md:text-6xl text-[#1F2937] leading-tight">
                        Professional tools, <br /><span className="text-[#1A4D2E]">simplified for everyone</span>
                    </h1>
                    <p className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
                        We've packed FictoInvest with institutional-grade technology, wrapped in a beautiful, easy-to-use interface.
                    </p>
                </div>
            </section>

            {/* Feature Grid */}
            <section className="py-20 px-6 md:px-12">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {allFeatures.map((feature, idx) => (
                            <div key={idx} className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-stone-100 group">
                                <div className={`w-14 h-14 ${feature.bg} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                                    <div className={feature.color}>{feature.icon}</div>
                                </div>
                                <h3 className="font-serif text-xl font-bold text-[#1F2937] mb-3">{feature.title}</h3>
                                <p className="text-gray-600 leading-relaxed text-sm">
                                    {feature.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Interactive CTA */}
            <section className="py-20 px-6 md:px-12 bg-[#1A4D2E] text-white overflow-hidden relative">
                <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-[#C1F23D] rounded-full opacity-20 blur-3xl"></div>
                <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-96 h-96 bg-green-400 rounded-full opacity-20 blur-3xl"></div>

                <div className="max-w-4xl mx-auto text-center relative z-10 space-y-8">
                    <h2 className="font-serif text-3xl md:text-5xl">Start your trading journey today</h2>
                    <p className="text-xl text-green-100">Join 2,000+ others who are mastering the markets.</p>
                    <Button
                        onClick={() => navigate('/register')}
                        className="bg-[#C1F23D] text-[#1A4D2E] hover:bg-[#b2e030] px-8 py-6 text-lg font-bold shadow-lg shadow-lime-900/20"
                    >
                        Create Free Account <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-6 md:px-12 border-t border-stone-200 bg-white">
                <div className="max-w-7xl mx-auto text-center">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-[#1A4D2E] rounded-lg flex items-center justify-center">
                                <TrendingUp className="w-5 h-5 text-white" />
                            </div>
                            <span className="font-serif text-xl font-semibold text-[#1A4D2E]">FictoInvest</span>
                        </div>
                        <div className="flex gap-8">
                            <Link to="/features" className="text-gray-600 hover:text-[#1A4D2E]">Features</Link>
                            <Link to="/pricing" className="text-gray-600 hover:text-[#1A4D2E]">Pricing</Link>
                            <Link to="/about" className="text-gray-600 hover:text-[#1A4D2E]">About</Link>
                            <Link to="/contact" className="text-gray-600 hover:text-[#1A4D2E]">Contact</Link>
                            <Link to="/privacy" className="text-gray-600 hover:text-[#1A4D2E]">Privacy</Link>
                        </div>
                        <p className="text-sm text-gray-500">
                            © {new Date().getFullYear()} FictoInvest. All rights reserved.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default FeaturesPage;
